import React, { forwardRef } from "react";

import { Heading, type HeadingProps } from "../Heading";
import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Page
 * -----------------------------------------------------------------------------------------------*/

type PageProps = React.ComponentPropsWithRef<"div">;

export const Page = forwardRef<HTMLDivElement, PageProps>(({ className, children, ...pageProps }, forwardedRef) => (
  <div {...pageProps} ref={forwardedRef} className={cn("flex h-dvh w-dvw flex-1 flex-col overflow-hidden", className)}>
    {children}
  </div>
));

Page.displayName = "Page";

/* -------------------------------------------------------------------------------------------------
 * Header
 * -----------------------------------------------------------------------------------------------*/

type HeaderProps = React.ComponentPropsWithRef<"header">;

const Header = React.forwardRef<HTMLElement, HeaderProps>(({ className, ...props }, forwardedref) => (
  <header
    {...props}
    ref={forwardedref}
    className={cn("m-2.5 gap-2.5 md:mx-10 md:my-5 md:gap-5 flex flex-wrap items-center", className)}
  />
));

Header.displayName = "Page.Header";

/* -------------------------------------------------------------------------------------------------
 * Title
 * -----------------------------------------------------------------------------------------------*/

type TitleProps = HeadingProps;

const Title = React.forwardRef<HTMLHeadingElement, TitleProps>((props, forwardedref) => (
  <Heading
    {...props}
    ref={forwardedref}
    className={cn("md:text-4xl overflow-hidden text-xl text-ellipsis whitespace-nowrap", props.className)}
  />
));

Title.displayName = "Page.Title";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = React.ComponentPropsWithRef<"main">;

const Content = React.forwardRef<HTMLElement, ContentProps>(({ className, children, ...props }, forwardedref) => (
  <main
    {...props}
    ref={forwardedref}
    className={cn(
      `mt-0 mr-2.5 mb-2.5 ml-2.5 md:mt-0 md:mr-10 md:mb-10 md:ml-10 md:rounded-[10px] flex-1 overflow-hidden rounded-[10px] bg-primary-white`,
      className,
    )}
  >
    {children}
  </main>
));

Content.displayName = "Page.Content";

const Root = Page;

export { Content, Header, Root, Title };
export type { ContentProps, HeaderProps, TitleProps };
